import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { LinkTarget } from '../constants/link-target.enum';
import { OfficeRoute } from '../constants/office-route.enum';
import { WdOriginApp, WdOriginPage, WdOriginType } from '../models/telemetry-constants.model';
import { ConfigParserService } from '../services/config-parser.service';
import { DocumentService } from '../services/document.service';
import { OfficeRouteService } from '../services/office-route.service';
import { WebPwaService } from '../services/web-pwa.service';
import { CapabilitiesService } from './capabilities.service';

export interface SharedContext {
    abtSignoutPath: string;
    activityLifetimeInSeconds: number;
    activityTrackingEnabled: boolean;
    accessTokenMaxRetryAttempts: number;
    consumerShellServiceEndpoint: string;
    createButtonTextCompleteStopCount: number;
    createButtonTextEmptyStopCount: number;
    createButtonTextTypingSpeed: number;
    dataUpdateCooldownInSeconds: number;
    dataUpdateIntervalInSeconds: number;
    dataUpdateMaxConsecutiveFailures: number;
    dataUpdateTimeoutInSeconds: number;
    dateOverride: string;
    defaultLinkTarget: LinkTarget;
    fluentHeroImageUrl: string;
    graphApiRoot: string;
    graphApiRootRefreshIntervalInSeconds: number;
    heroImageUrl: string;
    heroTitleGradientImageUrl: string;
    iceApplicationId: number;
    inactivityTimeoutInSeconds: number;
    inMockMode: boolean;
    metaResourcePath: string;
    msaAppId: string;
    msaTokenRedirectUrl: string;
    msaTokenTimeoutMs: number;
    officeHomeApiRoot: string;
    officeMarket: string;
    officeMarketLcid: string;
    officeStartManifestVersion: string;
    oneDriveConsumerApi: string;
    oneDriveResourceId: string;
    oneDriveUrl: string;
    outlookServiceRoot: string;
    recommendedIceApplicationId: number;
    serviceWorkerIntervalUpdateEnabled: boolean;
    sharePointResourceId: string;
    signOutRedirectUrl: string;
    signOutUrlTemplate: string;
    supportDocsUrl: string;
    teamsUrl: string;
    tokenLoginUrlTemplate: string;
    updateDataOnViewEnabled: boolean;
    usingOneDriveResourceAsSharePointResource: boolean;
    wacHostAppPrebootTimeout: number;
}

@Injectable({
    providedIn: 'root'
})
export class ContextService {
    public readonly sharedContext: SharedContext;
    public readonly isRtl: boolean;
    // This property is here in the absence of a service that is solely responsible for keeping app states. The isEdgeworth state
    // is required in a few services / components, but the value is known only after documents.component.ts has been initialized. Since these
    // classes don't share a common ancestor other than at the root level, the state has to be stored at a service that is common at the root.
    // isStandalone below also seems to fall in the same category, so if we have more such states, we should probably create a separate service for them.
    public isEdgeworth: boolean;
    public isNewConsumerUserExperience: boolean;

    private readonly defaultLinkTarget: LinkTarget;
    private isStandalone: boolean;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly cookieService: CookieService,
        private readonly documentService: DocumentService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly webPwaService: WebPwaService,
        configParserService: ConfigParserService
    ) {
        // Initialize to default values if there was failure to read user context from page response
        this.sharedContext = configParserService.parse<SharedContext>('sharedContext') || {
            abtSignoutPath: null,
            activityLifetimeInSeconds: 0,
            activityTrackingEnabled: false,
            accessTokenMaxRetryAttempts: 0,
            consumerShellServiceEndpoint: null,
            createButtonTextCompleteStopCount: 0,
            createButtonTextEmptyStopCount: 0,
            createButtonTextTypingSpeed: 0,
            dataUpdateCooldownInSeconds: 0,
            dataUpdateIntervalInSeconds: 0,
            dataUpdateMaxConsecutiveFailures: 0,
            dataUpdateTimeoutInSeconds: 0,
            dateOverride: null,
            defaultLinkTarget: LinkTarget.NewTab,
            fluentHeroImageUrl: null,
            graphApiRoot: null,
            graphApiRootRefreshIntervalInSeconds: 0,
            heroImageUrl: null,
            heroTitleGradientImageUrl: null,
            iceApplicationId: -1,
            inactivityTimeoutInSeconds: 60,
            inMockMode: false,
            metaResourcePath: null,
            msaAppId: null,
            msaTokenRedirectUrl: null,
            msaTokenTimeoutMs: 0,
            officeHomeApiRoot: null,
            officeMarket: 'en-us',
            officeMarketLcid: '1033',
            officeStartManifestVersion: null,
            oneDriveConsumerApi: null,
            oneDriveResourceId: null,
            oneDriveUrl: null,
            outlookServiceRoot: null,
            recommendedIceApplicationId: -1,
            serviceWorkerIntervalUpdateEnabled: false,
            sharePointResourceId: null,
            signOutRedirectUrl: null,
            signOutUrlTemplate: null,
            supportDocsUrl: null,
            teamsUrl: null,
            tokenLoginUrlTemplate: null,
            updateDataOnViewEnabled: false,
            usingOneDriveResourceAsSharePointResource: false,
            wacHostAppPrebootTimeout: 0
        };

        const html = this.documentService.nativeDocument.getElementsByTagName('html');
        this.isRtl = html && html[0] && html[0].getAttribute('dir') === 'rtl';

        // Save the default so if we change contexts we keep the initial setting
        this.defaultLinkTarget = this.sharedContext.defaultLinkTarget;
        this.webPwaService.isStandalone.subscribe(isStandalone => {
            this.isStandalone = isStandalone;
            this.sharedContext.defaultLinkTarget = isStandalone && !this.webPwaService.isOfficeApp() ? LinkTarget.SameFrame : this.defaultLinkTarget;
        });

        // Temporary fix related to ICM: https://portal.microsofticm.com/imp/v3/incidents/details/375504528/home
        // We started receiving locales with underscores instead of dashes so to mitigate the issue
        // we are converting them to the expected dash format
        const market = this.sharedContext.officeMarket;
        if (market && market.indexOf('-') === -1 && market.indexOf('_') >= 0 ) {
            this.sharedContext.officeMarket = market.split('_').join('-');
        }
    }

    public getCsrfToken(): string {
        return this.cookieService.get('AjaxSessionKey');
    }

    public getWdOrigin(type: WdOriginType): string {
        return `OFFICECOM-${this.getWdOriginApp()}.${this.getWdOriginPage()}.${type}`;
    }

    public getCurrentPageForLogging(): string {
        return this.officeRouteService.getCurrentPageForLogging();
    }

    public getWdOriginApp(): WdOriginApp {
        if (this.isStandalone) {
            return WdOriginApp.PWA;
        } else if (this.capabilitiesService.capabilities.pwaEnabled) {
            return WdOriginApp.HWA;
        } else if (this.capabilitiesService.capabilities.isEmbedMode) {
            return WdOriginApp.Embed;
        }

        return WdOriginApp.Web;
    }

    private getWdOriginPage(): WdOriginPage {
        const route = this.officeRouteService.getCurrentRoute();
        if (route) {
            switch (route.route) {
                case OfficeRoute.Start:
                    return WdOriginPage.Start;
                case OfficeRoute.Templates:
                    return WdOriginPage.Templates;
                case OfficeRoute.Content:
                    return WdOriginPage.Content;
                case OfficeRoute.Search:
                    return WdOriginPage.Search;
                default:
                    return WdOriginPage.Home;
            }
        }

        return WdOriginPage.Home;
    }
}
